import { useDebounce } from 'use-debounce';
import { Box, Button, Card, Chip, Container, Dialog, FormControl, MenuItem, Pagination, PaginationItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY, DEFAULT_PAGE_LIMIT } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import { getPrograms } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import { PinnacleBasicTable } from '../../components/PinnacleBasicTable/PinnacleBasicTable';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import { useUserForm } from './hooks/useUserForm';
import { PinnacleSearchField } from '../../components/PinnacleSearchField/PinnacleSearchField';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';
import { User } from '../../api/interfaces/User';
import { createUser, deleteUser, exportUsersPerProgram, getUsers, GetUsersParams, uploadBulkActiveDeactivateUsers, uploadBulkAssignSubjects, uploadBulkDeleteUsers, uploadBulkTransferUsersTerm, uploadBulkUsers } from '../../api/services/user';
import { LoadingMode } from '../../api/interfaces/Common';
import ManageBulkUsersMenu from './partials/ManageBulkUsersMenu';
import CreateUserDrawerContent from './partials/CreateUserDrawerContent';
import CreateBulkUsersDrawerContent from './partials/CreateBulkUsersDrawerContent';
import ActivateDeactivateBulkUsersDrawerContent from './partials/ActivateDeactivateBulkUsersDrawerContent';
import DeleteBulkUsersDrawerContent from './partials/DeleteBulkUsersDrawerContent';
import EditUserDrawerContent from './partials/EditUserDrawerContent/EditUserDrawerContent';
import { getSchools } from '../../api/services/school';
import { School } from '../../api/interfaces/School';
import TransferUsersTermUsersDrawerContent from './partials/TransferUsersTermUsersDrawerContent';
import AssignSubjectsBulkUsersDrawerContent from './partials/AssignSubjectsBulkUsersDrawerContent';

const usersColumn = [
    {
        key: 'firstName',
        label: 'FIRST NAME',
        render: (row: User) => row.firstName ? row.firstName : '--'
    },
    {
        key: 'lastName',
        label: 'LAST NAME',
        render: (row: User) => row.lastName ? row.lastName : '--'
    },
    { key: 'email', label: 'EMAIL ADDRESS' },
    {
        key: 'termName',
        label: 'TERM',
        sx: { minWidth: 150, maxWidth: 200 },
        render: (row: User) => row.termName ? row.termName : '--'
    },
    {
        key: 'programName',
        label: 'PROGRAM',
        sx: { minWidth: 150, maxWidth: 200 },
        render: (row: User) => row.programName ? row.programName : '--'
    },
    {
        key: 'isActive',
        label: 'STATUS',
        render: (row: User) => (
            <>
                {row.isActive ? (
                    <Chip
                        label="active"
                        variant="filled"
                        color="error"
                        sx={{
                            backgroundColor: '#D7F2E7',
                            color: '#028A4F',
                            fontSize: '12px'
                        }}
                    />
                ) : (
                    <Chip
                        label="inactive"
                        variant="filled"
                        color="error"
                        sx={{
                            backgroundColor: '#D8D8DD',
                            color: '#818181',
                            fontSize: '12px'
                        }}
                    />
                )}
            </>
        ),
        skipTooltip: true
    },
    {
        key: 'isTrial',
        label: 'TRIAL',
        render: (row: User) => (
            <>
                {row.isTrial && (
                    <Chip
                        label="trial"
                        variant="filled"
                        color="error"
                        sx={{
                            backgroundColor: '#FA3D3D33',
                            color: '#FA3D3D',
                            fontSize: '12px'
                        }}
                    />
                )}
            </>
        ),
        skipTooltip: true
    },
];

const UsersPage: React.FC = () => {
    const navigate = useNavigate();

    // Initial form data
    const initialFormData = useMemo(() => ({
        email: '',
        isTrial: false,
        isAdmin: false
    }), []);

    // Initial touch fields for error validation
    const initialTouchFields = {
        email: false,
        isTrial: false,
        isAdmin: false
    };

    // Hooks for form and validation states
    const {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        isFormValid
    } = useUserForm({
        initialFormData,
    });

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [programs, setPrograms] = useState<Program[]>([]);
    const [schools, setSchools] = useState<School[]>([]); // In preparation to edit mode to supply the schools right ahead
    const [users, setUsers] = useState<User[]>([]);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    // Search user with debounce
    const [searchUser, setSearchUser] = useState<string>('');
    const [debouncedSearchUser] = useDebounce(searchUser, 300);

    const [isSearching, setIsSearching] = useState(false);

    // Loading mode to handle different loading states
    const [loadingMode, setLoadingMode] = useState<LoadingMode>('none');

    // State for filtering users by program
    const [selectedProgramFilter, setSelectedProgramFilter] = useState<string>('');
    const [selectedProgramNameFilter, setSelectedProgramNameFilter] = useState<string>('');

    // Drawable states for create
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    // Set the selected user id for delete state
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [selectedUserName, setSelectedUserName] = useState<string>('');
    const [selectedUserInfo, setSelectedUserInfo] = useState<User | null>(null);
    // Use the correct type for the ref to access handleSubmit
    const editUserRef = useRef<{ handleSubmit: () => void } | null>(null);

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Bulk create users state
    const [bulkCreateUsersIsTrial, setBulkCreateUsersIsTrial] = useState<boolean>(false);
    const [bulkCreateUsersSelectedFile, setBulkCreateUsersSelectedFile] = useState<File | null>(null);
    const bulkCreateUsersFileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Bulk activate/deactivate users state
    const [bulkActivateDeactivateUsersIsActive, setBulkActivateDeactivateUsersIsActive] = useState<boolean>(false);
    const [bulkActivateDeactivateUsersSelectedFile, setBulkActivateDeactivateUsersSelectedFile] = useState<File | null>(null);
    const bulkActivateDeactivateUsersFileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Bulk delete users state
    const [bulkDeleteUsersSelectedFile, setBulkDeleteUsersSelectedFile] = useState<File | null>(null);
    const bulkDeleteUsersFileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Bulk transfer users term state
    const [bulkTransferUsersTermTermId, setBulkTransferUsersTermTermId] = useState<string>('');
    const [bulkTransferUsersTermSelectedFile, setBulkTransferUsersTermSelectedFile] = useState<File | null>(null);
    const bulkTransferUsersTermFileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Bulk assign subjects state
    const [bulkAssignSubjectsProgramId, setBulkAssignSubjectsProgramId] = useState<string>('');
    const [bulkAssignSubjectsSubjectIds, setBulkAssignSubjectsSubjectIds] = useState<string[]>([]);
    const [bulkAssignSubjectsSelectedFile, setBulkAssignSubjectsSelectedFile] = useState<File | null>(null);
    const bulkAssignSubjectsFileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Handle the opening and closing of toggle
    const toggleDrawer = (
        open: boolean, state: 'create' | 'edit' | 'create-bulk-users' | 'bulk-activate-deactivate-users' | 'bulk-delete-users' | 'bulk-transfer-users-term' | 'bulk-assign-subjects' | string,
        labelName?: string
    ) => () => {
        if (open) {
            setCurrentDrawableState(state);
            const title = state === 'create' ? 'Add New User' : state === 'edit' ? `Update User - ${labelName}` : state === 'create-bulk-users' ? 'Add Bulk Users' : state === 'bulk-activate-deactivate-users' ? 'Set Status of Bulk Users' : state === 'bulk-delete-users' ? 'Delete Bulk Users' : state === 'bulk-transfer-users-term' ? 'Transfer Term' : state === 'bulk-assign-subjects' ? 'Add Subjects' : '';
            setCurrentDrawableTitle(title);
        } else {
            setCurrentDrawableState('')
        }

        setIsDrawerOpen(open);
    };

    const fetchUsers = useCallback(async (
        pageNumber = 1,
        pageLimit = DEFAULT_PAGE_LIMIT,
        programIdParam = selectedProgramFilter,
        keywordParam = debouncedSearchUser
    ) => {
        try {
            const params: GetUsersParams = {
                page: pageNumber,
                limit: pageLimit,
                programId: programIdParam || undefined,
                keyword: keywordParam || undefined
            };

            const usersResponse = await getUsers(params);

            setUsers(usersResponse.data.data);
            setPage(pageNumber);
            setTotalPages(usersResponse.data.totalPages || 0);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, [selectedProgramFilter, debouncedSearchUser]);

    const fetchPrograms = useCallback(async () => {
        try {
            const programResponse = await getPrograms();
            setPrograms(programResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchSchools = useCallback(async () => {
        try {
            const schoolResponse = await getSchools();
            setSchools(schoolResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchSearchResults = useCallback(async (pageNumber = 1) => {
        try {
            setIsSearching(true);
            await fetchUsers(pageNumber, DEFAULT_PAGE_LIMIT, selectedProgramFilter, debouncedSearchUser);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsSearching(false);
        } finally {
            setIsSearching(false);
        }
    }, [fetchUsers, selectedProgramFilter, debouncedSearchUser]);

    const handleCreateSubmit = async () => {
        setIsSubmitting(true);
        try {
            const createResponse = await createUser({
                ...formData,
                isTrial: true
            });
            setUsers((prevUsers) => [createResponse.data, ...prevUsers]);
            pushNotification({
                details: createResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
            setLoadingMode('none');
        } finally {
            setIsSubmitting(false);
        }
    };

    const disableActionButtons = loadingMode === 'deleting' || loadingMode === 'paginating' || loadingMode === 'submitting' || isSubmitting;

    const handleEdit = (selectedUser: User) => {
        setSelectedUserId(selectedUser._id);
        setSelectedUserInfo(selectedUser);
        toggleDrawer(true, 'edit', selectedUser.email)();
    };

    // Open the dialog propmt to confirm
    const handleDelete = (user: User) => {
        setSelectedUserId(user._id);
        setSelectedUserName(`${user.firstName} ${user.lastName} - ${user.email}`);
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteUser(selectedUserId);
            setUsers(prevUsers => prevUsers.filter(user => user._id !== selectedUserId));
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedUserName('');
            setSelectedUserId('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
            setLoadingMode('none');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handlePageChange = async (newPage: number) => {
        try {
            // Do not fetch if the page is already the same
            if (newPage !== page) {
                setPage(newPage);
                setLoadingMode('paginating');
                await fetchUsers(newPage, DEFAULT_PAGE_LIMIT, selectedProgramFilter, searchUser);
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setLoadingMode('none');
        } finally {
            setLoadingMode('none');
        }
    }

    const handleUpdateUser = (updatedUser: User, skipClose?: boolean) => {
        setUsers((prev) =>
            prev.map((u) => (u._id === updatedUser._id ? updatedUser : u))
        );

        if (!skipClose) {
            setSelectedUserId('');
            setSelectedUserInfo(null);
            toggleDrawer(false, currentDrawableState)();
        }
    };

    const handleCloseDrawer = () => {
        if (!isSubmitting) {
            toggleDrawer(false, currentDrawableState)();
        }
        setBulkCreateUsersSelectedFile(null);
        setBulkActivateDeactivateUsersSelectedFile(null);
        setBulkDeleteUsersSelectedFile(null);
        setBulkTransferUsersTermSelectedFile(null);
    }

    const handleFilterUsersByProgramChange = async (event: SelectChangeEvent<string>) => {
        setLoadingMode('filtering');
        try {
            const programId = event.target.value;
            const programName = programs.find(p => p._id === programId)?.name || '';

            setSearchUser('');
            setSelectedProgramFilter(programId);
            setSelectedProgramNameFilter(programName);
            setPage(1);

            await fetchUsers(1, DEFAULT_PAGE_LIMIT, programId);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsSubmitting(false);
            setLoadingMode('none');
        } finally {
            setLoadingMode('none');
        }
    };

    const clearState = () => {
        setLoadingMode('none');
        setIsSubmitting(false);
    };

    const handleCreateBulkUsersSubmit = async () => {
        setIsSubmitting(true);
        setLoadingMode('submitting');
        if (bulkCreateUsersFileInputRef.current?.files?.[0]) {
            try {
                const file = bulkCreateUsersFileInputRef.current.files[0];
                const uploadBulkUsersResponse = await uploadBulkUsers(file, bulkCreateUsersIsTrial);

                // Fetch updated users and reset filters
                await fetchUsers();
                setBulkCreateUsersSelectedFile(null);
                setBulkCreateUsersIsTrial(false);

                if (bulkCreateUsersFileInputRef.current) {
                    bulkCreateUsersFileInputRef.current.value = '';
                }
                setSearchUser('');
                setSelectedProgramFilter('');
                setSelectedProgramNameFilter('');
                pushNotification({
                    details: uploadBulkUsersResponse.message,
                    type: NotificationTypes.Success,
                });
                toggleDrawer(false, '')();
                clearState();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubmitting(false);
                setLoadingMode('none');
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleActivateDeactiveBulkUsersSubmit = async () => {
        setIsSubmitting(true);
        setLoadingMode('submitting');
        if (bulkActivateDeactivateUsersFileInputRef.current?.files?.[0]) {
            try {
                const file = bulkActivateDeactivateUsersFileInputRef.current.files[0];
                const uploadBulkUsersResponse = await uploadBulkActiveDeactivateUsers(file, bulkActivateDeactivateUsersIsActive);

                // Fetch updated users and reset filters
                await fetchUsers();
                setBulkActivateDeactivateUsersSelectedFile(null);
                setBulkActivateDeactivateUsersIsActive(false);

                if (bulkActivateDeactivateUsersFileInputRef.current) {
                    bulkActivateDeactivateUsersFileInputRef.current.value = '';
                }
                setSearchUser('');
                setSelectedProgramFilter('');
                setSelectedProgramNameFilter('');
                pushNotification({
                    details: uploadBulkUsersResponse.message,
                    type: NotificationTypes.Success,
                });
                toggleDrawer(false, '')();
                clearState();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubmitting(false);
                setLoadingMode('none');
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleDeleteBulkUsersSubmit = async () => {
        setIsSubmitting(true);
        setLoadingMode('submitting');
        if (bulkDeleteUsersFileInputRef.current?.files?.[0]) {
            try {
                const file = bulkDeleteUsersFileInputRef.current.files[0];
                const uploadBulkUsersResponse = await uploadBulkDeleteUsers(file);

                // Fetch updated users and reset filters
                await fetchUsers();
                setBulkDeleteUsersSelectedFile(null);

                if (bulkDeleteUsersFileInputRef.current) {
                    bulkDeleteUsersFileInputRef.current.value = '';
                }

                setSearchUser('');
                setSelectedProgramFilter('');
                setSelectedProgramNameFilter('');
                pushNotification({
                    details: uploadBulkUsersResponse.message,
                    type: NotificationTypes.Success,
                });
                toggleDrawer(false, '')();
                clearState();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubmitting(false);
                setLoadingMode('none');
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleTransferTermsUsersSubmit = async () => {
        setIsSubmitting(true);
        setLoadingMode('submitting');

        if (bulkTransferUsersTermFileInputRef.current?.files?.[0]) {
            try {
                const file = bulkTransferUsersTermFileInputRef.current.files[0];
                const uploadBulkUsersResponse = await uploadBulkTransferUsersTerm(file, bulkTransferUsersTermTermId);

                // Fetch updated users and reset filters
                await fetchUsers();
                setSearchUser('');
                setSelectedProgramFilter('');
                setSelectedProgramNameFilter('');
                pushNotification({
                    details: uploadBulkUsersResponse.message,
                    type: NotificationTypes.Success,
                });
                toggleDrawer(false, '')();
                clearState();
                setBulkTransferUsersTermSelectedFile(null);
                setBulkTransferUsersTermTermId('');

                if (bulkTransferUsersTermFileInputRef.current) {
                    bulkTransferUsersTermFileInputRef.current.value = '';
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubmitting(false);
                setLoadingMode('none');
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleAssignSubjectsBulkUsersSubmit = async () => {
        setIsSubmitting(true);
        setLoadingMode('submitting');

        if (bulkAssignSubjectsFileInputRef.current?.files?.[0]) {
            try {
                const file = bulkAssignSubjectsFileInputRef.current.files[0];
                const uploadBulkUsersResponse = await uploadBulkAssignSubjects(file, {
                    programId: bulkAssignSubjectsProgramId,
                    subjectIds: bulkAssignSubjectsSubjectIds,
                });

                // Fetch updated users and reset filters
                await fetchUsers();
                setSearchUser('');
                setSelectedProgramFilter('');
                setSelectedProgramNameFilter('');
                pushNotification({
                    details: uploadBulkUsersResponse.message,
                    type: NotificationTypes.Success,
                });
                toggleDrawer(false, '')();
                clearState();
                setBulkAssignSubjectsSelectedFile(null);
                setBulkAssignSubjectsProgramId('');
                setBulkAssignSubjectsSubjectIds([]);

                if (bulkAssignSubjectsFileInputRef.current) {
                    bulkAssignSubjectsFileInputRef.current.value = '';
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubmitting(false);
                setLoadingMode('none');
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleExportUsersPerProgram = async () => {
        setIsSubmitting(true);
        setLoadingMode('submitting');
        try {
            const response = await exportUsersPerProgram({ programId: selectedProgramFilter });

            const blob = response.data;
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];
            let filename = `users-export-under-${selectedProgramNameFilter}-${formattedDate}.xlsx`.toLowerCase();

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
            setLoadingMode('none');
        } finally {
            setIsSubmitting(false);
            setLoadingMode('none');
        }
    }

    // Debouncing mechanism for search user
    useEffect(() => {
        if (debouncedSearchUser.trim() === '') return;

        const search = async () => {
            setLoadingMode('searching');
            setIsSearching(true);
            try {
                await fetchSearchResults();
            } finally {
                setIsSearching(false);
                setLoadingMode('none');
            }
        };

        search();
    }, [debouncedSearchUser, fetchSearchResults]);

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                setLoadingMode('fetching');
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }

                setCurrentAdminName(firstName);
                await fetchPrograms();
                await fetchUsers();
                await fetchSchools();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setLoadingMode('none');
            }
        };
        run();
    }, [fetchPrograms, fetchUsers, fetchSchools, navigate]);

    return (
        <main className="main-content">
            <Sidebar />
            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Users' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        {/* Left side: New + Manage Bulk + Search */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                startIcon={<PlaylistAddOutlinedIcon />}
                                sx={{ mr: 2 }}
                                onClick={toggleDrawer(true, 'create')}
                                disabled={disableActionButtons}
                            >
                                New User
                            </Button>

                            <ManageBulkUsersMenu
                                toggleDrawer={toggleDrawer}
                                disableActionButtons={disableActionButtons}
                            />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <PinnacleSearchField
                                    id="searchUsers"
                                    label="Search users here..."
                                    value={searchUser}
                                    onChange={setSearchUser}
                                    isLoading={isSearching}
                                    disabled={disableActionButtons}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            p: 3,
                            backgroundColor: '#fff',
                            boxShadow: '0px 4px 12px 8px rgba(211, 211, 211, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1" fontWeight={600}>
                                    List of users {selectedProgramNameFilter && `under ${selectedProgramNameFilter}`}
                                </Typography>

                                {(selectedProgramFilter && users && loadingMode === 'none') && (
                                    <Button
                                        variant="contained"
                                        className="btn-white"
                                        startIcon={<SummarizeOutlinedIcon />}
                                        onClick={handleExportUsersPerProgram}
                                        disabled={disableActionButtons}
                                        size="small"
                                    >
                                        Export
                                    </Button>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: '#818181' }}>
                                    view by
                                </Typography>
                                <FormControl>
                                    <Select
                                        value={selectedProgramFilter}
                                        onChange={handleFilterUsersByProgramChange}
                                        defaultValue="All Programs"
                                        disabled={disableActionButtons}
                                        displayEmpty
                                        inputProps={{ 'aria-label': 'Select Program' }}
                                        sx={{
                                            minWidth: 250,
                                            color: '#818181',
                                            '.MuiSelect-icon': {
                                                color: '#818181',
                                            },
                                        }}
                                        MenuProps={{
                                            disableScrollLock: true,
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="">All Programs</MenuItem>
                                        {programs.map((program) => (
                                            <MenuItem key={program._id} value={program._id}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block',
                                                    }}
                                                >
                                                    {program.name}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>


                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ opacity: isSearching ? 0.4 : 1 }}>
                                <PinnacleBasicTable
                                    columns={usersColumn}
                                    rows={users}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    headerColor="#FE6A35"
                                    hasMultiSelect={false}
                                    disabledActions={disableActionButtons}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, newPage) => handlePageChange(newPage)}
                                        shape="rounded"
                                        renderItem={(item) => (
                                            <PaginationItem
                                                {...item}
                                                sx={{
                                                    border: "1px solid #D8D8DD",
                                                    color: "#818181",
                                                    "&.Mui-selected": {
                                                        backgroundColor: "#D8D8DD !important",
                                                        color: "#000 !important",
                                                        "&:hover": {
                                                            backgroundColor: "#D8D8DD",
                                                        },
                                                    },
                                                    "&:not(.Mui-selected)": {
                                                        color: "#818181",
                                                    },
                                                    "&.Mui-disabled": {
                                                        opacity: 0.4,
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                            {(isSearching || loadingMode === 'paginating' || loadingMode === 'filtering' || loadingMode === 'submitting') && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                />
                            )}
                        </Box>
                    </Card>
                </Container>
            </Box>

            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isDrawerOpen}
                onClose={handleCloseDrawer}
                customWidthSize={currentDrawableState === 'edit' ? 700 : undefined}
                onSubmit={() => editUserRef.current?.handleSubmit()}
                onSubmitName={currentDrawableState === 'edit' ? 'UPDATE INFORMATION' : ''}
                isSubmitting={isSubmitting}
            >
                {currentDrawableState === 'create' && (
                    <CreateUserDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleCreateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                    />
                )}

                {currentDrawableState === 'edit' && (
                    <EditUserDrawerContent
                        ref={editUserRef}
                        programs={programs}
                        schools={schools}
                        selectedUserId={selectedUserId}
                        selectedUserInfo={selectedUserInfo}
                        onClose={() => toggleDrawer(false, currentDrawableState)()}
                        onSuccess={handleUpdateUser}
                        setIsSubmitting={setIsSubmitting}
                        isSubmitting={isSubmitting}
                    />
                )}

                {currentDrawableState === 'create-bulk-users' && (
                    <CreateBulkUsersDrawerContent
                        isSubmitting={isSubmitting}
                        onSubmit={handleCreateBulkUsersSubmit}
                        ref={bulkCreateUsersFileInputRef}
                        selectedFile={bulkCreateUsersSelectedFile}
                        onFileChange={setBulkCreateUsersSelectedFile}
                        isTrial={bulkCreateUsersIsTrial}
                        setIsTrial={setBulkCreateUsersIsTrial}
                    />
                )}

                {currentDrawableState === 'bulk-activate-deactivate-users' && (
                    <ActivateDeactivateBulkUsersDrawerContent
                        isSubmitting={isSubmitting}
                        onSubmit={handleActivateDeactiveBulkUsersSubmit}
                        ref={bulkActivateDeactivateUsersFileInputRef}
                        selectedFile={bulkActivateDeactivateUsersSelectedFile}
                        onFileChange={setBulkActivateDeactivateUsersSelectedFile}
                        isActive={bulkActivateDeactivateUsersIsActive}
                        setIsActive={setBulkActivateDeactivateUsersIsActive}
                    />
                )}

                {currentDrawableState === 'bulk-delete-users' && (
                    <DeleteBulkUsersDrawerContent
                        isSubmitting={isSubmitting}
                        onSubmit={handleDeleteBulkUsersSubmit}
                        ref={bulkDeleteUsersFileInputRef}
                        selectedFile={bulkDeleteUsersSelectedFile}
                        onFileChange={setBulkDeleteUsersSelectedFile}
                    />
                )}

                {currentDrawableState === 'bulk-transfer-users-term' && (
                    <TransferUsersTermUsersDrawerContent
                        isSubmitting={isSubmitting}
                        onSubmit={handleTransferTermsUsersSubmit}
                        ref={bulkTransferUsersTermFileInputRef}
                        selectedFile={bulkTransferUsersTermSelectedFile}
                        onFileChange={setBulkTransferUsersTermSelectedFile}
                        termId={bulkTransferUsersTermTermId}
                        setTermId={setBulkTransferUsersTermTermId}
                    />
                )}

                {currentDrawableState === 'bulk-assign-subjects' && (
                    <AssignSubjectsBulkUsersDrawerContent
                        isSubmitting={isSubmitting}
                        onSubmit={handleAssignSubjectsBulkUsersSubmit}
                        ref={bulkAssignSubjectsFileInputRef}
                        selectedFile={bulkAssignSubjectsSelectedFile}
                        onFileChange={setBulkAssignSubjectsSelectedFile}
                        programs={programs}
                        programId={bulkAssignSubjectsProgramId}
                        setProgramId={setBulkAssignSubjectsProgramId}
                        subjectIds={bulkAssignSubjectsSubjectIds}
                        setSubjectIds={setBulkAssignSubjectsSubjectIds}
                    />
                )}

            </PinnacleDrawer>

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete User"
                    itemToDelete={selectedUserName}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </main>
    );
};

export default UsersPage;
